import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';
import lodashKeyBy from 'lodash/fp/keyBy';

import {
} from 'common/userRoles';

import {
  getDaysOnMarket,
  makeListingFullAddressString,
  shouldHideSunlockCodes,
} from 'common/listingHelpers';
import {
  numberWithCommas,
  formatPrice,
} from 'common/formatters';
import {
  transformBooleanToYesNo,
} from 'client/helpers/formikHelpers';
import {
  LISTING_LINKS_TO_SCRAPE,
  LISTING_MLS_STATUS_TEMPORARILY_OFF_MARKET,
  LISTING_WALKTHROUGH_CONDITION_ITEMS,
} from 'common/listingConstants';
import getListingFormFields from 'common/listingFormFields';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrCopyText from 'client/components/SrCopyText';
import SrPhotoViewer from 'client/components/SrPhotoViewer';

import branch from 'client/core/branch';

const defaultFormatter = (listingFormField, listing) => {
  const value = listing[listingFormField.id];

  if (typeof value === 'boolean') {
    return transformBooleanToYesNo(value);
  }
  if (listingFormField.type === 'currency') {
    return formatPrice(value);
  }

  return `${value || ''}`;
};

const makeListingList = ({ listing, fieldgroup }) => {
  const listingFormFields = getListingFormFields({ fieldgroup });
  const listingFormFieldsById = lodashKeyBy('id', listingFormFields);

  return fieldgroup
    .filter((listingFieldId) => listingFormFieldsById[listingFieldId])
    .map((listingFieldId) => {
      const listingFormField = listingFormFieldsById[listingFieldId];

      return {
        label: listingFormField.title,
        value: defaultFormatter(listingFormField, listing),
      };
    });
};

const SrListingList = ({ title, list }) => (
  <SrBox className='section titled twoColumns'>
    <SrText font='whitney-sc' weight='bold'>{title}</SrText>
    <SrBox className='contentContainer zebra'>
      {list.map(({ label, value }, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <SrFlex key={index} className='row' justify='between'>
          <span className='left'>{label}</span>
          <span className='right'>{value || '--'}</span>
        </SrFlex>
      ))}
    </SrBox>
  </SrBox>
);

@branch({
  appUrl: ['env', 'APP_URL'],
  currentUser: ['currentUser'],
})
class SrListingAdminList extends Component {
  static propTypes = {
    appUrl: PropTypes.string.isRequired,
    listing: PropTypes.object.isRequired,
    marketingListing: PropTypes.object,
    market: PropTypes.object,
    partner: PropTypes.object,
    partnerUsers: PropTypes.array,
    lease: PropTypes.object,
    apartment: PropTypes.object,
    sunlock: PropTypes.object,
    listingLinks: PropTypes.array,
    listingAdminInfo: PropTypes.object.isRequired,
    redirection: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
  }

  _onToggleOpen = () => {
    this.setState({
      isOpen: !this.state.isOpen,
    });
  }

  render() {
    const {
      appUrl,
      listing,
      marketingListing,
      market,
      partner,
      partnerUsers,
      lease,
      apartment,
      sunlock,
      listingLinks,
      listingAdminInfo,
      redirection,
      currentUser,
    } = this.props;

    const {
      isOpen,
    } = this.state;

    const hideSunlockCodes = shouldHideSunlockCodes(listing, currentUser);

    return (
      <div>
        <SrFlex className='section titled'>
          <SrText weight='bold' font='whitney-sc' className='title'>
            {'admin info - '}
            <SrText
              weight='bold'
              font='whitney-sc'
              color='blue300'
              cursor='pointer'
              decoration='underline'
              onClick={this._onToggleOpen}
            >
              {isOpen ? 'collapse' : 'expand'}
            </SrText>
          </SrText>
        </SrFlex>
        {isOpen && (
          <div>
            <SrListingList
              title=''
              list={lodashCompact([
                { label: 'Address', value: makeListingFullAddressString(listing) },
                { label: 'Apartment Name', value: listing.apartmentName },
                { label: 'Partner', value: listing.listingManagementCompany },
                { label: 'Price', value: `$${numberWithCommas(listing.price)}` },
                { label: 'Available Date', value: moment.utc(listing.availableDate).format('MMMM Do, YYYY') },
                {
                  label: 'Available For Setup Date',
                  value: moment.utc(listing.availableForSetupDate).format('MMMM Do, YYYY'),
                },
                {
                  label: 'Vacant Date',
                  value: moment.utc(listing.vacantDate).format('MMMM Do, YYYY'),
                },
                { label: 'MLS Status', value: listing.mlsStatus },
                marketingListing && marketingListing.status === LISTING_MLS_STATUS_TEMPORARILY_OFF_MARKET
                  ? {
                    label: 'Temp. off Market',
                    value: marketingListing.bringBackToMarketDate
                      ? moment.utc(marketingListing.bringBackToMarketDate).format('MMMM Do, YYYY')
                      : null,
                  }
                  : null,
                { label: 'Occupant type', value: listing.occupantType },
                { label: 'Days on Market', value: getDaysOnMarket(listing) || '--' },
              ])}
            />
            {marketingListing && (
              <SrListingList
                title='submitted details'
                list={[
                  {
                    label: 'Date',
                    value: moment.utc(marketingListing.createdDate).format('MMMM Do, YYYY'),
                  },
                  {
                    label: 'User',
                    value: marketingListing.user ? marketingListing.user.name : null,
                  },
                ]}
              />
            )}
            <SrListingList
              title='ids & listing status'
              list={lodashCompact([
                partner ? {
                  label: 'Partner Listing Page',
                  value: (
                    <SrLink
                      color='blue300'
                      target='_blank'
                      to={`${appUrl}/partner/${partner.slug}/listing/${listing.id}`}
                    >
                      {`${appUrl}/partner/${partner.slug}/listing/${listing.id}`}
                    </SrLink>
                  ) } : null,
                { label: 'MLS Number', value: listing.mlsNumber },
                {
                  label: 'Apply online',
                  value: (
                    <SrCopyText
                      text={`${appUrl}/listing/${listing.id}?apply=true`}
                    />
                  ),
                },
              ])}
            />
            {lease && (
              <SrListingList
                title='lease'
                list={[
                  { label: 'Move-in Date', value: moment.utc(lease.startDate).format('MMMM Do, YYYY') },
                  {
                    label: 'Household',
                    value: (
                      <SrLink
                        color='blue300'
                        target='_blank'
                        to={`${appUrl}/admin/household/all/${lease.renterUserId}`}
                      >
                        {`${appUrl}/admin/household/all/${lease.renterUserId}`}
                      </SrLink>
                    ),
                  },
                  { label: 'Price', value: `$${numberWithCommas(lease.rentPrice)}` },
                ]}
              />
            )}
            <SrListingList
              title='structure summary'
              list={[
                { label: 'Property Type', value: listing.propertyType },
              ]}
            />
            <SrListingList
              title='address configuration'
              list={[
                { label: 'Market ID', value: market ? market.displayName : '--' },
                { label: 'Street Display', value: listing.addressStreetDisplay },
                { label: 'Street Name', value: listing.addressStreetName },
                { label: 'Street Suffix', value: listing.addressStreetSuffix },
                { label: 'Has No Suffix', value: transformBooleanToYesNo(listing.addressStreetNoSuffix) },
                { label: 'Street Number', value: listing.addressStreetNumber },
                { label: 'Unit #', value: listing.apartmentUnit },
                { label: 'Zip Code', value: listing.addressZip },
                { label: 'City', value: listing.addressCity },
                { label: 'State', value: listing.addressStateCode },
                { label: 'County', value: listing.addressCounty },
                { label: 'Country', value: listing.addressCountryCode },
                { label: 'Latitude', value: listing.locationLatitude },
                { label: 'Longitude', value: listing.locationLongitude },
              ]}
            />
            {apartment && (
              <SrListingList
                title='apartment'
                list={[
                  { label: 'Apartment Name', value: apartment.name },
                  { label: 'Units in Complex', value: apartment.numberOfUnits },
                  { label: 'Complex Building', value: listing.apartmentBuildingId },
                  { label: 'Apartment Description', value: apartment.fieldsForDisplay.listingFields.description }, // eslint-disable-line max-len
                  { label: 'Fitness Center', value: transformBooleanToYesNo(apartment.fieldsForDisplay.listingFields.hasFitnessCenter) || '--' }, // eslint-disable-line max-len
                  { label: 'Dog Park', value: transformBooleanToYesNo(apartment.fieldsForDisplay.listingFields.hasDogWalkPark) || '--' }, // eslint-disable-line max-len
                  { label: 'Swimming Pool', value: transformBooleanToYesNo(apartment.fieldsForDisplay.listingFields.hasPool) || '--' }, // eslint-disable-line max-len
                  { label: 'Garage', value: transformBooleanToYesNo(apartment.fieldsForDisplay.listingFields.hasGarage) || '--' }, // eslint-disable-line max-len
                  {
                    label: 'Job Setup Instructions',
                    value: apartment.jobSetupInstructions,
                  },
                ]}
              />
            )}
            {partner && (
              <SrListingList
                title='partner'
                list={[
                  {
                    label: 'Users',
                    value: partnerUsers
                      .map(({ user }) => `${user.name} (${user.email || 'no-email'} ${user.phone || 'no-phone'})`) // eslint-disable-line max-len
                      .join(', ') || '--',
                  },
                  {
                    label: 'Contact for Application Approvals',
                    value: partnerUsers
                      .filter(({ applicationApprovalsAlert }) => applicationApprovalsAlert)
                      .map(({ user }) => `${user.name} (${user.email || 'no-email'} ${user.phone || 'no-phone'})`) // eslint-disable-line max-len
                      .join(', ') || '--',
                  },
                  {
                    label: 'Security Deposit Delivery address',
                    value: partnerUsers
                      .filter(({ securityDepositDeliveryAlert }) => securityDepositDeliveryAlert)
                      .map(({ user }) => `${user.name} (${user.email || 'no-email'} ${user.phone || 'no-phone'})`) // eslint-disable-line max-len
                      .join(', ') || '--',
                  },
                  {
                    label: 'Security Deposit Delivery instructions',
                    value: partner.securityDepositDeliveryInstructions,
                  },
                  { label: 'Security Deposit Delivery Method', value: partner.securityDepositDeliveryMethod },
                  { label: 'Approval Hold Time', value: partner.approvalHoldTime },
                  {
                    label: 'Lease Template File',
                    value: partner.leaseTemplateFileUrl ? (
                      <SrLink
                        key={partner.leaseTemplateFileUrl}
                        color='blue300'
                        target='_blank'
                        to={`/pdf-viewer?pdfUrl=${partner.leaseTemplateFileUrl}`}
                      >
                        {partner.leaseTemplateFileUrl}
                      </SrLink>
                    ) : null,
                  },
                  { label: 'Lease Signing Description', value: partner.leaseSigningDescription },
                  { label: 'Lease Signing System', value: partner.leaseSignSystem },
                  { label: 'Lease Signing Login Email', value: partner.leaseSignSystemUserName },
                  { label: 'Lease Signing Login Password', value: partner.leaseSignSystemPassword },
                ]}
              />
            )}
            <SrListingList
              title='interior unit details'
              list={makeListingList({
                listing: listingAdminInfo,
                fieldgroup: [
                  'hasWasher',
                  'hasDryer',
                  'hasTileFlooring',
                  'hasWoodFlooring',
                  'hasCarpetFlooring',
                  'hasConcreteFlooring',
                  'hasVinylFlooring',
                  'hasRefrigerator',
                  'hasGasStove',
                  'hasElectricStove',
                  'hasMicrowave',
                ],
              })}
            />
            <SrListingList
              title='exterior unit details'
              list={makeListingList({
                listing: listingAdminInfo,
                fieldgroup: [
                  'hasGarage',
                  'parkingSpaces',
                  'hasFencedYards',
                  'hasPool',
                ],
              })}
            />
            <SrListingList
              title='data'
              list={makeListingList({
                listing: listingAdminInfo,
                fieldgroup: [
                  'hasCommunalWasherDryer',
                  'hasDogWalkPark',
                  'kitchenCountertopMaterial',
                  'hasBusinessCenter',
                  'hasConferenceRoom',
                  'hasJoggingTrail',
                  'hasRoofDeck',
                  'elevatorInBuilding',
                  'hasStackedWasherDryer',
                  'hasOnlinePayments',
                  'floorLevel',
                  'hasWheelchairAccessible',
                  'coveredParkingCount',
                  'surfaceParkingCount',
                  'garageAttachedParkingSpots',
                  'garageDetachedParkingSpots',
                  'hasOnSiteManagement',
                  'hasFencedYards',
                  'hasYard',
                  'hasControlledBuildingAccess',
                  'hasCeilingFans',
                  'hasFireplace',
                  'hasExtraStorage',
                  'hasWalkInClosets',
                  'kitchenAppliancesColor',
                  'hasShowerTub',
                  'numberOfFloors',
                  'valetTrashFee',
                  'pestControlFee',
                  'wasteWaterFee',
                  'monthlyCommunityFee',
                  'monthlyCommunityFeeDescription',
                  'carportFee',
                  'reservedParkingSpotFee',
                  'bikeRackFee',
                  'electricFee',
                  'gasFee',
                  'waterAndSewerFee',
                  'cableAndInternetCombinedFee',
                  'cableFee',
                  'internetFee',
                  'packageLockerSystemFee',
                  'coveredParkingFee',
                  'storageFee',
                  'surfaceParkingFee',
                  'garageAttachedParkingSpotFee',
                  'garageDetachedParkingSpotFee',
                ],
              })}
            />
            <SrListingList
              title='touring access'
              list={[
                { label: 'Is Unit tourable?', value: transformBooleanToYesNo(!listingAdminInfo.cantTourFlag) }, // eslint-disable-line max-len
                { label: 'Occupant Type', value: listingAdminInfo.occupantType },
                { label: 'Occupant Name', value: listingAdminInfo.occupantName },
                { label: 'Occupant Phone', value: listingAdminInfo.occupantPhone },
                { label: 'Occupant Email', value: listingAdminInfo.occupantEmail },
                { label: 'Key Access', value: listingAdminInfo.propertySetupAccessType },
                { label: 'Lockbox Code', value: listingAdminInfo.lockboxCode },
                { label: 'Gate Code', value: listingAdminInfo.gateCode },
                { label: 'Alarm Code', value: listingAdminInfo.alarmCode },
                { label: 'Special Showing Instructions', value: listingAdminInfo.showingInstructions },
              ]}
            />
            <SrListingList
              title='application requirements'
              list={[
                { label: 'Lease Min Length', value: listingAdminInfo.minLeaseMonths },
                { label: 'Lease Max Length', value: listingAdminInfo.maxLeaseMonths },
                {
                  label: 'Lease Length Negotiable?',
                  value: transformBooleanToYesNo(listingAdminInfo.leaseLengthNegotiable),
                },
                {
                  label: 'Accepts Guarantors?',
                  value: transformBooleanToYesNo(listingAdminInfo.acceptsGuarantor),
                },
                {
                  label: 'Enrolled in and accept Section 8?',
                  value: transformBooleanToYesNo(listingAdminInfo.section8) },
                {
                  label: 'Tenant Criteria Doc.',
                  value: (
                    <div>
                      {(listingAdminInfo.tenantCriteriaFileUrls || []).map((fileUrl) => (
                        <SrLink
                          key={fileUrl}
                          color='blue300'
                          target='_blank'
                          to={`/pdf-viewer?pdfUrl=${fileUrl}`}
                        >
                          {fileUrl}
                        </SrLink>
                      ))}
                    </div>
                  ),
                },
              ]}
            />
            <SrListingList
              title='field operations data'
              list={[
                {
                  label: 'Ready to be listed?',
                  value: listingAdminInfo.walkthroughConditions
                    .map((condition) => {
                      const item = LISTING_WALKTHROUGH_CONDITION_ITEMS
                        .find((item) => item.value === condition) || {};
                      return item.label;
                    }).join(', '),
                },
                { label: 'Walkthrough Notes', value: listingAdminInfo.walkthroughNotes },
                {
                  label: 'Walkthrough Photos',
                  value: listingAdminInfo.walkthroughPhotoUrls && listingAdminInfo.walkthroughPhotoUrls.length ? ( // eslint-disable-line max-len
                    <SrPhotoViewer photoUrls={listingAdminInfo.walkthroughPhotoUrls}>
                      View
                    </SrPhotoViewer>
                  ) : null,
                },
                { label: 'Yardsign?', value: transformBooleanToYesNo(listingAdminInfo.yardSignAllowed) },
                {
                  label: 'Yardsign URL',
                  value: redirection ? (
                    <SrLink
                      color='blue300'
                      target='_blank'
                      to={`/${redirection.id}`}
                    >
                      {redirection.id}
                    </SrLink>
                  ) : null,
                },
                {
                  label: 'Yard Sign Photo',
                  value: listingAdminInfo.yardSignPhotoUrls && listingAdminInfo.yardSignPhotoUrls.length ? (
                    <SrPhotoViewer photoUrls={listingAdminInfo.yardSignPhotoUrls}>
                      View
                    </SrPhotoViewer>
                  ) : null,
                },
                {
                  label: 'Sunlock Mastercode',
                  value: sunlock && !hideSunlockCodes ? sunlock.partnerLockCode : null,
                },
                {
                  label: 'Sunlock Backup code',
                  value: sunlock && !hideSunlockCodes ? sunlock.lockCode : null,
                },
                { label: 'Sunlock Serial', value: sunlock ? sunlock.raspberryPiSerialNumber : null },
                {
                  label: 'Sunlock Photo',
                  value: listingAdminInfo.sunlockPhotoUrls && listingAdminInfo.sunlockPhotoUrls.length ? (
                    <SrPhotoViewer photoUrls={listingAdminInfo.sunlockPhotoUrls}>
                      View
                    </SrPhotoViewer>
                  ) : null,
                },
                { label: 'Lockbox Type', value: listingAdminInfo.lockboxType },
                { label: 'Lockbox Location', value: listingAdminInfo.lockboxLocation },
                {
                  label: 'Lockbox Photo (Supra)',
                  value: listingAdminInfo.lockboxPhotoUrls && listingAdminInfo.lockboxPhotoUrls.length
                    ? (
                      <SrPhotoViewer photoUrls={listingAdminInfo.lockboxPhotoUrls}>
                        View
                      </SrPhotoViewer>
                    )
                    : '--',
                },
                { label: 'Igloohome Lock Id', value: listing.igloohomeLockId },
              ]}
            />
            {listingLinks.length > 0 && (
              <SrListingList
                title='marketing pages'
                list={LISTING_LINKS_TO_SCRAPE.map(({ siteName }) => ({
                  label: siteName,
                  value: (listingLinks.find((link) => siteName === link.siteName) || {}).linkUrl,
                })).filter(({ value }) => value).map(({ label, value }) => ({
                  label,
                  value: (
                    <SrLink color='blue300' target='_blank' to={value}>
                      {value}
                    </SrLink>
                  ),
                }))}
              />
            )}
          </div>
        )}
      </div>
    );
  }
}

export default SrListingAdminList;
